import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/Button';
import { apiUrl } from '@/lib/env';

interface AuthMethods {
  authMode: 'local' | 'bitrix24';
  methods: Array<'bitrix24' | 'local'>;
  localAuthAvailable: boolean;
}

export function LocalLoginPage() {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [methods, setMethods] = useState<AuthMethods | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetch(apiUrl('/auth/methods'), { credentials: 'include' })
      .then((res) => res.json())
      .then((data: AuthMethods) => {
        setMethods(data);
        if (!data.localAuthAvailable) {
          setErrorMessage('Резервный вход отключён');
        }
      })
      .catch(() => {
        setMethods({ authMode: 'bitrix24', methods: ['bitrix24'], localAuthAvailable: false });
        setErrorMessage('Не удалось получить методы аутентификации');
      });
  }, []);

  const handleLocalLogin = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setErrorMessage(null);
      setIsLoading(true);

      try {
        const response = await fetch(apiUrl('/auth/local/login'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ username, password }),
        });

        if (response.ok) {
          navigate('/', { replace: true });
          return;
        }

        const data = (await response.json().catch(() => ({}))) as {
          error?: { code?: string };
        };
        const errorMessages: Record<string, string> = {
          local_auth_disabled: 'Резервный вход отключён',
          local_auth_misconfigured: 'Ошибка конфигурации сервера',
          rate_limit_exceeded: 'Слишком много попыток входа. Повторите позже.',
          invalid_credentials: 'Неверный логин или пароль',
          invalid_request: 'Неверный формат запроса',
        };
        setErrorMessage(errorMessages[data?.error?.code ?? ''] ?? 'Не удалось выполнить вход');
      } catch {
        setErrorMessage('Ошибка соединения с сервером');
      } finally {
        setIsLoading(false);
      }
    },
    [username, password, navigate],
  );

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 16px',
    fontSize: '14px',
    border: '1px solid var(--border)',
    borderRadius: '8px',
    backgroundColor: 'var(--background)',
    color: 'var(--text-primary)',
    outline: 'none',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '14px',
    fontWeight: 500,
    color: 'var(--text-secondary)',
    marginBottom: '6px',
  };

  const formDisabled = isLoading || methods?.localAuthAvailable === false;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '24px',
        background: 'var(--background)',
      }}
    >
      <div
        style={{
          maxWidth: '420px',
          width: '100%',
          padding: '48px 32px',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <h1
            style={{
              fontSize: '24px',
              fontWeight: 700,
              color: 'var(--text-primary)',
              marginBottom: '8px',
            }}
          >
            AXIUM meet
          </h1>
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
            Резервный вход по логину и паролю
          </p>
        </div>

        {errorMessage && (
          <div
            role="alert"
            style={{
              padding: '12px 16px',
              marginBottom: '24px',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '8px',
              color: 'var(--text-primary)',
              fontSize: '14px',
            }}
          >
            {errorMessage}
          </div>
        )}

        <form onSubmit={handleLocalLogin}>
          <div style={{ marginBottom: '16px' }}>
            <label htmlFor="username" style={labelStyle}>
              Логин
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              style={inputStyle}
              autoComplete="username"
              autoFocus
              required
              disabled={formDisabled}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label htmlFor="password" style={labelStyle}>
              Пароль
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={inputStyle}
              autoComplete="current-password"
              required
              disabled={formDisabled}
            />
          </div>

          <Button
            type="submit"
            variant="primary"
            disabled={formDisabled}
            style={{ width: '100%', marginBottom: '16px' }}
          >
            {isLoading ? 'Вход...' : 'Войти'}
          </Button>

          <div style={{ textAlign: 'center' }}>
            <Link
              to="/login"
              style={{
                color: 'var(--text-tertiary)',
                fontSize: '12px',
                textDecoration: 'underline',
              }}
            >
              Вернуться к основному входу
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
